import React from "react"
import { Empty } from "antd"
import { UseTableInstanceProps } from "react-table"
import { Row, TableRow } from "./row"
import { Header, Footer } from "."
import { FixedSizeList as List } from "react-window"

interface BodyProps extends UseTableInstanceProps<object> {
	paginated?: boolean
	expanded?: boolean
	onRowPress?: Function
	renderEmpty?: React.ReactNode
	renderExpanded?: React.ReactNode
	emptyDescription?: React.ReactNode
	alwaysExpanded?: boolean
	getRowStyle?: (row: any) => React.CSSProperties
}

// Threshold for when to enable virtualization
const VIRTUALIZATION_THRESHOLD = 100
const ROW_HEIGHT = 49

// Virtualized row renderer component
const VirtualizedRowRenderer = React.memo(
	({
		index,
		style,
		data,
	}: {
		index: number
		style: React.CSSProperties
		data: {
			rows: TableRow[]
			prepareRow: (row: any) => void
			expanded: boolean
			onRowPress?: Function
			renderExpanded?: React.ReactNode
			alwaysExpanded?: boolean
			getRowStyle?: (row: any) => React.CSSProperties
		}
	}) => {
		const {
			rows,
			prepareRow,
			expanded,
			onRowPress,
			renderExpanded,
			alwaysExpanded,
			getRowStyle,
		} = data
		const rowProps = rows[index]
		prepareRow(rowProps)

		// Get custom row style (e.g., background color for selection/errors)
		const customRowStyle = getRowStyle ? getRowStyle(rowProps) : undefined

		// Combine react-window positioning style with custom row style
		// The positioning (position, top, left, width, height) comes from react-window
		// Custom styles (backgroundColor, etc.) come from getRowStyle
		const combinedStyle: React.CSSProperties = {
			...style,
			...customRowStyle,
			// Ensure the row displays as flex to match non-virtualized layout
			display: "flex",
			alignItems: "center",
		}

		return (
			<Row
				{...rowProps}
				index={index}
				onPress={onRowPress}
				expandable={expanded}
				renderExpanded={renderExpanded}
				alwaysExpanded={alwaysExpanded}
				style={combinedStyle}
			/>
		)
	}
)

VirtualizedRowRenderer.displayName = "VirtualizedRowRenderer"

// Functional wrapper component for virtualized body
const VirtualizedBody: React.FC<{
	rows: TableRow[]
	prepareRow: (row: any) => void
	expanded: boolean
	onRowPress?: Function
	renderExpanded?: React.ReactNode
	alwaysExpanded?: boolean
	getRowStyle?: (row: any) => React.CSSProperties
	height: number
}> = ({
	rows,
	prepareRow,
	expanded,
	onRowPress,
	renderExpanded,
	alwaysExpanded,
	getRowStyle,
	height,
}) => {
	const itemData = {
		rows,
		prepareRow,
		expanded,
		onRowPress,
		renderExpanded,
		alwaysExpanded,
		getRowStyle,
	}

	return (
		<List
			height={height}
			itemCount={rows.length}
			itemSize={ROW_HEIGHT}
			width="100%"
			itemData={itemData}
			overscanCount={5}
			style={{ flex: 1 }}
		>
			{VirtualizedRowRenderer}
		</List>
	)
}

export class Body extends React.PureComponent<
	BodyProps | any,
	{ containerHeight: number }
> {
	containerRef = React.createRef<HTMLDivElement>()
	resizeObserver: ResizeObserver | null = null

	constructor(props: any) {
		super(props)
		this.state = {
			containerHeight: 600, // Default height
		}
	}

	componentDidMount() {
		this.updateContainerHeight()
		// Set up ResizeObserver to handle container size changes
		if (this.containerRef.current && typeof ResizeObserver !== "undefined") {
			this.resizeObserver = new ResizeObserver(() => {
				this.updateContainerHeight()
			})
			this.resizeObserver.observe(this.containerRef.current)
		}
	}

	componentWillUnmount() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect()
		}
	}

	updateContainerHeight = () => {
		if (this.containerRef.current) {
			const height = this.containerRef.current.clientHeight
			if (height > 0 && height !== this.state.containerHeight) {
				this.setState({ containerHeight: height })
			}
		}
	}

	get rows() {
		// @ts-ignore
		const { rows, page, paginated } = this.props
		return paginated ? page : rows
	}

	get isEmpty() {
		return this.rows.length === 0
	}

	get shouldVirtualize() {
		// Only virtualize when we have many rows and no drag functionality
		const { drag } = this.props
		return this.rows.length > VIRTUALIZATION_THRESHOLD && !drag
	}

	renderRow = (rowProps: TableRow, index: any) => {
		const {
			prepareRow,
			expanded,
			onRowPress,
			renderExpanded,
			drag,
			onDragEnd,
			alwaysExpanded,
			getRowStyle,
		} = this.props
		prepareRow(rowProps)
		return (
			<Row
				key={`table_row_${rowProps.id}`}
				{...rowProps}
				drag={drag}
				index={index}
				onDragEnd={onDragEnd}
				onPress={onRowPress}
				expandable={expanded}
				renderExpanded={renderExpanded}
				alwaysExpanded={alwaysExpanded}
				style={getRowStyle ? getRowStyle(rowProps) : undefined}
			/>
		)
	}

	renderVirtualizedRows() {
		const {
			prepareRow,
			expanded,
			onRowPress,
			renderExpanded,
			alwaysExpanded,
			getRowStyle,
		} = this.props

		// Use the full container height for the virtualized list
		const listHeight = Math.max(this.state.containerHeight, 400)

		return (
			<VirtualizedBody
				rows={this.rows}
				prepareRow={prepareRow}
				expanded={expanded}
				onRowPress={onRowPress}
				renderExpanded={renderExpanded}
				alwaysExpanded={alwaysExpanded}
				getRowStyle={getRowStyle}
				height={listHeight}
			/>
		)
	}

	renderContent() {
		const { drag } = this.props
		if (!this.isEmpty)
			return (
				<React.Fragment>
					<Header
						drag={drag}
						headerGroups={this.props.headerGroups}
						resizable={this.props.resizable}
						sortable={this.props.sortable}
					/>
					<div
						ref={this.containerRef}
						style={{
							minHeight:
								this.props.hasFiltration &&
								(this.props.filtrationApi?.defaultValues?.columnSearch ||
									(Array.isArray(this.props.data) &&
										this.props.data.length > 0))
									? "calc(100% - 92px)"
									: "calc(100% - 40px)",
							// For virtualized tables, use flex to fill available space
							...(this.shouldVirtualize
								? {
										flex: 1,
										display: "flex",
										flexDirection: "column" as const,
									}
								: {}),
						}}
						className="TableBodyRows"
					>
						{this.shouldVirtualize
							? this.renderVirtualizedRows()
							: this.rows.map(this.renderRow)}
					</div>
				</React.Fragment>
			)

		const { renderEmpty, emptyDescription } = this.props

		return (
			<div
				className="TableBodyEmpty"
				style={{
					padding: "40px 0",
					height:
						this.props.hasFiltration &&
						(this.props.filtrationApi?.defaultValues?.columnSearch ||
							(Array.isArray(this.props.data) && this.props.data.length > 0))
							? "calc(100% - 55px)"
							: "100%",
					justifyContent: "center",
					display: "flex",
					alignItems: "center",
				}}
			>
				{typeof renderEmpty === "function" ? (
					renderEmpty()
				) : (
					<Empty description={emptyDescription} />
				)}
			</div>
		)
	}

	render() {
		const {
			getTableBodyProps,
			isSinglePage,
			fullPage,
			data,
			disableOverflowY,
		} = this.props

		const style = { overflowY: "auto" } as any

		if (isSinglePage) {
			style.height = "calc(100% - 190px)"
		}

		if (fullPage) {
			style.height = "100%"
		}

		if (disableOverflowY) {
			style.overflowY = "hidden"
		}

		// When virtualized, the List handles its own scrolling
		if (this.shouldVirtualize) {
			style.overflowY = "hidden"
			style.display = "flex"
			style.flexDirection = "column"
		}

		return (
			<div
				style={{
					overflowX: "auto",
					flex: 1,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div
					{...(getTableBodyProps() as any)}
					style={style}
					className={`TableBody`}
				>
					{this.renderContent()}
					{!this.props.noFieldFilter &&
						this.props.hasFiltration &&
						(this.props.filtrationApi?.defaultValues?.columnSearch ||
							(Array.isArray(data) && data.length > 0)) && (
							<Footer
								{...this.props}
								filtrationApi={this.props.filtrationApi}
								headerGroups={this.props.headerGroups}
							/>
						)}
				</div>
			</div>
		)
	}
}
